import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * useBoardDrag — pointer-based vertical drag reorder for the compact board.
 *
 * Rules:
 * - grip pointerdown starts a drag immediately (mouse, pen, or touch).
 * - Row-level pointerdown starts a drag on mouse/pen only. Touch users drag
 *   from the grip so a long-press on a cell keeps opening the highlight
 *   popover.
 * - Threshold: once the pointer travels 3 px, the drag is committed — an
 *   insertion line appears and pointerup fires `onDrop`.
 * - Cross-section drops are refused with a toast; the row is not moved.
 *
 * Consumer supplies:
 * - `readRowFromDom(element)` → { sectionKey, kind, id, version } | null
 * - `onDrop(moving, sectionKey, after)` where
 *   `moving = { kind, id }`, `after = { kind, id } | { kind: null, id: null }`.
 *
 * Returned:
 * - `gripProps(meta)` — spread onto the grip button.
 * - `rowProps(meta)` — spread onto the row <tr> (mouse/pen only).
 * - `indicator` — a JSX node rendered fixed-position at the current
 *   insertion line; render once at the page root.
 */
export function useBoardDrag({ readRowFromDom, onDrop }) {
  const [dragging, setDragging] = useState(null); // {moving, sectionKey}
  const [indicator, setIndicator] = useState(null); // {top, left, width}
  const insertionRef = useRef(null); // { kind, id } | null — row we'd land after
  const stateRef = useRef({ moving: null, startY: 0, startX: 0, sectionKey: "", moved: false });

  const cleanup = useCallback(() => {
    stateRef.current = { moving: null, startY: 0, startX: 0, sectionKey: "", moved: false };
    insertionRef.current = null;
    setDragging(null);
    setIndicator(null);
  }, []);

  const findRowUnderPointer = useCallback((clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return null;
    let cur = el;
    while (cur && cur !== document.body) {
      const meta = readRowFromDom(cur);
      if (meta) return { meta, rect: cur.getBoundingClientRect() };
      cur = cur.parentElement;
    }
    return null;
  }, [readRowFromDom]);

  const onPointerMoveWindow = useCallback((event) => {
    const state = stateRef.current;
    if (!state.moving) return;

    if (!state.moved) {
      const dy = Math.abs(event.clientY - state.startY);
      const dx = Math.abs(event.clientX - state.startX);
      if (dy < 3 && dx < 3) return;
      state.moved = true;
      setDragging({ moving: state.moving, sectionKey: state.sectionKey });
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    }

    const hit = findRowUnderPointer(event.clientX, event.clientY);
    if (!hit) { setIndicator(null); insertionRef.current = null; return; }
    const { meta, rect } = hit;
    if (meta.sectionKey !== state.sectionKey) {
      setIndicator(null);
      insertionRef.current = { crossSection: true };
      return;
    }
    if (meta.kind === state.moving.kind && meta.id === state.moving.id) {
      setIndicator(null); insertionRef.current = null; return;
    }
    const inTopHalf = event.clientY < rect.top + rect.height / 2;
    setIndicator({
      top: inTopHalf ? rect.top : rect.bottom,
      left: rect.left,
      width: rect.width,
    });
    if (inTopHalf) {
      insertionRef.current = { kind: "before", targetKind: meta.kind, targetId: meta.id };
    } else {
      insertionRef.current = { kind: "after", targetKind: meta.kind, targetId: meta.id };
    }
  }, [findRowUnderPointer]);

  const onPointerUpWindow = useCallback(async () => {
    const state = stateRef.current;
    const insertion = insertionRef.current;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    if (!state.moving) return;
    if (!state.moved) {
      // No drag: silently drop the interaction (row/grip stays where it was).
      cleanup();
      return;
    }
    if (!insertion) { cleanup(); return; }
    if (insertion.crossSection) {
      toast.error("Move within the same lifecycle section.");
      cleanup();
      return;
    }
    // Compute `after`: if insertion.kind == "after" we anchor after target;
    // if "before" we anchor after the target's previous sibling in the same
    // section. The caller resolves "previous sibling" via readRowFromDom by
    // walking the DOM upstream.
    let after = null;
    if (insertion.kind === "after") {
      after = { kind: insertion.targetKind, id: insertion.targetId };
    } else {
      // Walk upstream to find previous row in same section
      const targetSelector = `[data-board-row-id="${insertion.targetId}"]`;
      const targetEl = document.querySelector(targetSelector);
      let prev = targetEl?.previousElementSibling;
      while (prev) {
        const meta = readRowFromDom(prev);
        if (meta && meta.sectionKey === state.sectionKey
            && !(meta.kind === state.moving.kind && meta.id === state.moving.id)) {
          after = { kind: meta.kind, id: meta.id };
          break;
        }
        prev = prev.previousElementSibling;
      }
      // No previous sibling in section → top-of-section (both null)
      if (!after) after = { kind: null, id: null };
    }
    const moving = state.moving;
    const sectionKey = state.sectionKey;
    cleanup();
    await onDrop(moving, sectionKey, after);
  }, [cleanup, onDrop, readRowFromDom]);

  useEffect(() => () => {
    // Ensure body cursor/select are reset if component unmounts mid-drag
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  const gripProps = useCallback((meta) => ({
    onPointerDown: (event) => {
      if (event.button !== 0 && event.button !== undefined && event.pointerType === "mouse") return;
      event.stopPropagation();
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* ignore */ }
      stateRef.current = {
        moving: { kind: meta.kind, id: meta.id, version: meta.version },
        startY: event.clientY,
        startX: event.clientX,
        sectionKey: meta.sectionKey,
        moved: false,
      };
      setDragging({ moving: stateRef.current.moving, sectionKey: meta.sectionKey });
      // Attach window listeners synchronously — critical for fast touch flicks
      // where pointermove can fire before React commits the useEffect.
      const handleUp = () => {
        window.removeEventListener("pointermove", onPointerMoveWindow);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleCancel);
        onPointerUpWindow();
      };
      const handleCancel = () => {
        window.removeEventListener("pointermove", onPointerMoveWindow);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleCancel);
        cleanup();
      };
      window.addEventListener("pointermove", onPointerMoveWindow);
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointercancel", handleCancel);
    },
  }), [cleanup, onPointerMoveWindow, onPointerUpWindow]);

  const rowProps = useCallback((meta) => ({
    onPointerDown: (event) => {
      // Row-level drag on mouse/pen only. Touch users drag from the grip on
      // the far left — long-press on a cell is reserved for the highlight
      // popover so the two gestures don't fight.
      if (event.pointerType && event.pointerType !== "mouse" && event.pointerType !== "pen") return;
      if (event.button !== 0 && event.button !== undefined) return;
      const t = event.target;
      if (t && t.closest && t.closest(
        'a, button, input, select, textarea, [role="button"], [contenteditable="true"], .stage-pill, .board-drag-handle'
      )) return;
      stateRef.current = {
        moving: { kind: meta.kind, id: meta.id, version: meta.version },
        startY: event.clientY,
        startX: event.clientX,
        sectionKey: meta.sectionKey,
        moved: false,
      };
      const handleUp = () => {
        window.removeEventListener("pointermove", onPointerMoveWindow);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleCancel);
        onPointerUpWindow();
      };
      const handleCancel = () => {
        window.removeEventListener("pointermove", onPointerMoveWindow);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleCancel);
        cleanup();
      };
      window.addEventListener("pointermove", onPointerMoveWindow);
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointercancel", handleCancel);
    },
  }), [cleanup, onPointerMoveWindow, onPointerUpWindow]);

  const indicatorNode = indicator ? (
    <div
      aria-hidden="true"
      data-testid="board-drag-indicator"
      style={{
        position: "fixed",
        top: indicator.top - 1,
        left: indicator.left,
        width: indicator.width,
        height: 2,
        background: "#2563eb",
        boxShadow: "0 0 0 1px rgba(37,99,235,0.35)",
        pointerEvents: "none",
        zIndex: 50,
      }}
    />
  ) : null;

  return { gripProps, rowProps, indicator: indicatorNode };
}
